import { useCallback, useEffect, useState } from "react";
import { useNavigate, NavigateFunction } from "react-router-dom";
import { adminBatchRunsList, adminBatchTrigger, type BatchRun } from "../api/client";

const PAGE_SIZE = 20;

const STATUS_LABEL: Record<string, string> = {
  succeeded: "Completato",
  failed: "Errore",
  running: "In corso…",
};

function statusVariant(status: string): string {
  switch (status) {
    case "succeeded": return "success";
    case "failed": return "danger";
    case "running": return "info";
    default: return "neutral";
  }
}

function fmtDate(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtDuration(s: number | null): string {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function StatusBadge({ status }: { status: string }) {
  return <span className={`badge badge-${statusVariant(status)}`}>{STATUS_LABEL[status] || status}</span>;
}

function numBadge(n: number, variant: "info" | "danger" | "neutral") {
  return <span className={`badge badge-${n > 0 ? variant : "neutral"}`}>{n}</span>;
}

const CHIPS: { key: keyof BatchRun["breakdown"]; label: string; icon: string; to: string; variant: "info" | "success" | "neutral" }[] = [
  { key: "rdi",     label: "Coda",    icon: "assignment",           to: "/",        variant: "info" },
  { key: "allarme", label: "Allarmi", icon: "notifications_active", to: "/allarmi", variant: "neutral" },
  { key: "hyper",   label: "Hyper",   icon: "domain",               to: "/hyper",   variant: "success" },
];

function BreakdownChips({ breakdown, navigate }: { breakdown?: BatchRun["breakdown"]; navigate: NavigateFunction }) {
  const bd = breakdown ?? { rdi: 0, allarme: 0, hyper: 0 };
  const chips = CHIPS.filter(c => bd[c.key] > 0);
  if (!chips.length) return <span className="text-muted" style={{ fontSize: "0.85em" }}>—</span>;
  return (
    <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {chips.map(c => (
        <button
          key={c.key}
          onClick={e => { e.stopPropagation(); navigate(c.to); }}
          title={`Vai a ${c.label}`}
          className={`badge badge-${c.variant}`}
          style={{ border: "none", cursor: "pointer" }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 13 }}>{c.icon}</span>
          {bd[c.key]} {c.label}
        </button>
      ))}
    </span>
  );
}

export function AdminEmailBatch() {
  const navigate = useNavigate();
  const [runs, setRuns]         = useState<BatchRun[]>([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [error, setError]       = useState("");
  const [pendingTs, setPendingTs] = useState<number | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const r = await adminBatchRunsList(page, PAGE_SIZE);
      setRuns(r.runs);
      setTotal(r.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore caricamento");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  // Polling silenzioso: niente spinner, niente flash
  useEffect(() => {
    const hasRunning = runs.some(r => r.status === "running");
    const waitingTrigger = pendingTs !== null && Date.now() - pendingTs < 60_000;
    if (!hasRunning && !waitingTrigger) return;
    const ms = waitingTrigger ? 3_000 : 5_000;
    const id = setInterval(() => {
      load(true);
      if (pendingTs && runs.some(r => new Date(r.started_at).getTime() >= pendingTs)) {
        setPendingTs(null);
      }
    }, ms);
    return () => clearInterval(id);
  }, [runs, load, pendingTs]);

  async function onTrigger() {
    setTriggering(true);
    setError("");
    try {
      await adminBatchTrigger();
      const ts = Date.now();
      setPendingTs(ts);
      // triggering rimane true finché il polling non trova la riga "running"
      // oppure scade il timeout di sicurezza (15s)
      setTimeout(() => setTriggering(false), 15_000);
      setTimeout(() => load(true), 2_000);
      setTimeout(() => load(true), 6_000);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg.includes("già in esecuzione") ? "Un batch è già in corso." : `Errore: ${msg}`);
      setTriggering(false);
    }
  }

  // Quando il polling trova la riga "running", spegni lo stato triggering
  useEffect(() => {
    if (triggering && runs.some(r => r.status === "running")) {
      setTriggering(false);
    }
  }, [runs, triggering]);

  const isRunning = triggering || runs.some(r => r.status === "running");
  const lastOk = runs.find(r => r.status === "succeeded");
  const pages  = Math.ceil(total / PAGE_SIZE);

  return (
    <div style={{ padding: "20px 32px", maxWidth: 1200 }}>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Elaborazione Email</h1>
          <p className="text-muted">
            Storico dei cicli di lettura email dalla casella <code>casadei_RI_trusty@mail.infocert.it</code>.
            Il sistema controlla automaticamente ogni ora.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={onTrigger}
          disabled={isRunning}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            {isRunning ? "hourglass_top" : "sync"}
          </span>
          {isRunning ? "In corso…" : "Controlla ora"}
        </button>
      </div>

      {/* Ultimo ciclo riuscito */}
      {lastOk && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>
          <span className="material-symbols-outlined" style={{ flexShrink: 0 }}>check_circle</span>
          <span>
            Ultimo ciclo completato: <strong>{fmtDate(lastOk.started_at)}</strong>
            {" "}— {lastOk.emails_found} email trovate, {lastOk.processed} nuove elaborate,{" "}
            {lastOk.skipped_dedup} già in DB, {lastOk.discarded} scartate
            {lastOk.duration_s != null && <> — {fmtDuration(lastOk.duration_s)}</>}
          </span>
        </div>
      )}

      {error && (
        <div className="alert alert-danger" style={{ marginBottom: 16 }}>
          <span className="material-symbols-outlined" style={{ flexShrink: 0 }}>error</span>
          {error}
        </div>
      )}

      {/* Tabella */}
      <div className="card" style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Avvio</th>
              <th>Trigger</th>
              <th>Stato</th>
              <th>Durata</th>
              <th style={{ textAlign: "right" }}>Email trovate</th>
              <th style={{ textAlign: "right" }}>Nuove elaborate</th>
              <th style={{ textAlign: "right" }}>Già in DB</th>
              <th style={{ textAlign: "right" }}>Scartate</th>
              <th style={{ textAlign: "right" }}>Errori</th>
              <th>Destinazione</th>
            </tr>
          </thead>
          <tbody>
            {loading && runs.length === 0 ? (
              <tr><td colSpan={10} className="text-center text-muted" style={{ padding: "var(--space-5)" }}>Caricamento…</td></tr>
            ) : runs.length === 0 ? (
              <tr><td colSpan={10} className="text-center text-muted" style={{ padding: "var(--space-5)" }}>
                Nessun ciclo registrato. Premi "Controlla ora" per il primo avvio.
              </td></tr>
            ) : runs.map(r => (
              <tr
                key={r.id}
                onClick={() => navigate("/")}
              >
                <td>{fmtDate(r.started_at)}</td>
                <td>
                  <span className="text-muted" style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.85em" }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 15 }}>
                      {r.triggered_by.startsWith("manual:") ? "person" : "schedule"}
                    </span>
                    {r.triggered_by.startsWith("manual:") ? "manuale" : "cron"}
                  </span>
                </td>
                <td><StatusBadge status={r.status} /></td>
                <td>{fmtDuration(r.duration_s)}</td>
                <td style={{ textAlign: "right" }}>{r.emails_found}</td>
                <td style={{ textAlign: "right" }}>{numBadge(r.processed, "info")}</td>
                <td className="text-muted" style={{ textAlign: "right" }}>{r.skipped_dedup}</td>
                <td className="text-muted" style={{ textAlign: "right" }}>{r.discarded}</td>
                <td style={{ textAlign: "right" }}>{numBadge(r.errors, "danger")}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <BreakdownChips breakdown={r.breakdown} navigate={navigate} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginazione */}
      {pages > 1 && (
        <div className="pagination">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Precedenti</button>
          <span style={{ alignSelf: "center" }}>Pagina {page} di {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>Successivi →</button>
        </div>
      )}

      {/* Nota scheduling */}
      <div className="flow-readonly-card text-sm text-muted" style={{ marginTop: 24 }}>
        <strong>Schedulazione automatica:</strong> il sistema controlla la casella ogni ora (cron <code>0 * * * *</code>).
        Le email già elaborate vengono riconosciute e saltate automaticamente.
        Solo le email nuove vengono processate e aggiunte alla dashboard.
      </div>
    </div>
  );
}
